import { useState } from 'react'
import { FileText, StickyNote } from 'lucide-react'
import { MentionTextarea } from '@/components/notes/MentionTextarea'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { formatNoteWhen } from '@/lib/utils/noteFormatting'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import type { PmsAuditRow } from '../types'

function formatChangeMessage(notes: Record<string, unknown> | undefined): string {
  if (!notes || typeof notes !== 'object') return ''
  const entries = Object.entries(notes)
  if (entries.length === 1 && entries[0][0] === 'note') {
    const value = entries[0][1]
    if (Array.isArray(value)) return String(value[1] ?? value[0] ?? '')
    return String(value ?? '')
  }
  return entries
    .map(([key, value]) => {
      if (Array.isArray(value) && value.length >= 2) {
        return `${key} : ${value[0] ?? '—'} => ${value[1] ?? '—'}`
      }
      if (Array.isArray(value) && value.length === 1) {
        return `${key} : ${value[0] ?? '—'}`
      }
      return `${key} : ${value ?? '—'}`
    })
    .join('\n')
}

export function PmsAuditLogSection({
  title = 'Audit Logs',
  rows,
  onSave,
  testId = 'pms-audit-log-section',
}: {
  title?: string
  rows: PmsAuditRow[]
  onSave?: (note: string) => Promise<void>
  testId?: string
}) {
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const saveNote = async () => {
    const note = draft.trim()
    if (!note || !onSave || saving) return
    setSaving(true)
    try {
      await onSave(note)
      setDraft('')
      showSuccess('Note saved.')
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section
      data-testid={testId}
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-[0_4px_12px_rgba(15,23,42,0.05)]"
    >
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="m-0 flex items-center gap-2.5 text-base font-bold uppercase tracking-tight text-slate-900">
          <span className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full border border-[#c7ddff] bg-[#dbeafe] text-[#2563eb]">
            <StickyNote className="h-4 w-4" />
          </span>
          {title}
        </h2>
        {onSave ? (
          <button
            type="button"
            disabled={!draft.trim() || saving}
            onClick={() => void saveNote()}
            className={cn(primaryEntityActionButtonClass, 'whitespace-nowrap')}
            data-testid={`${testId}-save`}
          >
            {saving ? 'Please wait…' : 'Save'}
          </button>
        ) : null}
      </div>

      {onSave ? (
        <MentionTextarea
          value={draft}
          onChange={setDraft}
          onTaggingAdd={() => undefined}
          rows={5}
          disabled={saving}
          className="min-h-[7.5rem] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[rgb(21,151,255)] focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
          placeholder="Add note/message (use @ to mention Liberty users)"
          dataTestId={`${testId}-textarea`}
        />
      ) : null}

      <div className="mt-6">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-black">
          Notes &amp; Messages
        </h3>
        {rows.length === 0 ? (
          <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-4 py-6 text-center text-sm text-slate-500">
            No notes or messages.
          </div>
        ) : (
          <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-2 py-3 sm:px-4">
            <ul className="divide-y divide-sky-100/80">
              {rows.map((entry, index) => (
                <li
                  key={`${entry.updated_at}-${index}`}
                  className="grid grid-cols-1 gap-3 py-3 text-sm text-slate-700 first:pt-1 sm:grid-cols-12 sm:gap-0"
                >
                  <div className="flex gap-2 border-sky-200 sm:col-span-3 sm:border-r sm:pr-3">
                    <span className="mt-0.5 shrink-0 text-slate-500" aria-hidden>
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-slate-800">
                        {entry.updated_by || 'System'}
                      </div>
                      <div className="mt-1 text-xs text-slate-600">
                        {formatNoteWhen(entry.updated_at)}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0 break-words border-sky-200 px-0 text-xs font-medium text-slate-800 sm:col-span-2 sm:border-r sm:px-3">
                    {entry.entity_label || '\u00A0'}
                  </div>
                  <div className="min-w-0 whitespace-pre-wrap break-words pl-0 text-slate-800 sm:col-span-7 sm:pl-4 sm:pr-2">
                    {formatChangeMessage(entry.notes)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}
